const baseUrl = 'https://www.google.com/recaptcha/api.js?render='
const id = 'recaptcha-v3'

const recaptcha = {
  // The load is remembered as a PROMISE rather than as the resolved instance. Several forms
  // warm it up at the same moment — the manual vacancy screen carries the job form and the
  // add-company modal, both `data-controller="recaptcha"` — and keying on the resolved value
  // let each of them start its own download of the same script.
  loadPromise: null,

  siteKey () {
    return document.querySelector('meta[name=recaptcha-v3-site-key]')?.content
  },

  // Fetch Google's script while the form is being filled in instead of when it is submitted
  // (`recaptcha-controller` calls this on the first focus). Measured on the stand 2026-08-12:
  // with the script absent, `execute()` alone costs 560-800 ms because grecaptcha has to
  // bootstrap itself first; with it already loaded, 150-185 ms. That difference used to be
  // paid on EVERY submit, in the window between the press and the request, with nothing on
  // screen — most of the "adding a company takes two seconds".
  preload () {
    const siteKey = this.siteKey()

    if (!siteKey) return null

    if (!this.loadPromise) {
      this.loadPromise = this.load(siteKey).catch((error) => {
        // A failed load must not be remembered: otherwise every later submit on the page
        // inherits the same rejection and no token is ever fetched again.
        this.loadPromise = null
        throw error
      })
    }

    return this.loadPromise
  },

  async getToken (action) {
    const siteKey = this.siteKey()

    // reCAPTCHA not configured (disabled, or no site key in this environment):
    // skip gracefully so the form still submits. Server-side verification is what
    // enforces reCAPTCHA, and it stays in force wherever a site key is present.
    if (!siteKey) return ''

    const instance = await this.preload()

    // v3 tokens are single-use and short-lived, and `execute` mints a fresh one per call —
    // so the script is loaded once per page and kept. It used to be torn down after every
    // submit (`reset()`), which bought nothing but the badge disappearing and made the next
    // submit pay the bootstrap again.
    try {
      return await instance.execute(siteKey, { action })
    } catch (error) {
      // Forget the SCRIPT too, not just this token. Only a failed *load* used to be
      // forgotten (see `preload`), so an instance that loaded fine and went stale later —
      // a page left open long enough for Google's session to expire is the usual way —
      // stayed memoised, and every submit after it was handed the same broken object.
      // That is a form nothing but a manual page reload can revive, and it fails in
      // silence: measured on the stand 2026-08-27, two submits in a row produced two
      // identical console errors, no request, and nothing at all on screen.
      this.loadPromise = null
      throw error
    }
  },

  setToken (input, token) {
    input.value = token
  },

  load (siteKey) {
    return new Promise((resolve, reject) => {
      const scriptElement = document.createElement('script')
      scriptElement.id = id
      scriptElement.src = baseUrl + siteKey

      scriptElement.onload = () => {
        // Settle either way. A script that loads without leaving `grecaptcha` behind used to
        // leave the promise pending forever, which is a submit button that never comes back.
        if (!window.grecaptcha) {
          reject(new Error('reCAPTCHA script loaded without a grecaptcha object'))
          return
        }

        window.grecaptcha.ready(() => resolve(window.grecaptcha))
      }

      scriptElement.onerror = (error) => {
        reject(error)
      }

      document.head.append(scriptElement)
    })
  }
}

export const useRecaptcha = controller => {
  Object.assign(controller, { recaptcha })
}
